import React, { useCallback, useEffect, useState } from "react";
import ProspectProfileModal from "./Mapping/ProspectProfileModal";
import {
  useFetchAccountRelationship,
  useRefreshAccountRelationship,
} from "../hooks/useAccountRelationship";
import { useMainStore } from "@/stores";
import { useParams } from "react-router-dom";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { Bell, Clipboard, File } from "lucide-react";
import TakeNoteDrawer from "./Mapping/TakeNoteDrawer";
import CampaignDrawer from "./Mapping/CampaignDrawer";
import FollowUpReminderDrawer from "./Mapping/FollowUpReminderDrawer";
import ShowDraftModal from "./ShowDraftModal";
import MappingListSidebar from "./Mapping/MappingListSidebar";
import MappingLeftSideArea from "./Mapping/MappingLeftSideArea";

const RelationshipMapping = ({ height }) => {
  const { user } = useMainStore();
  const { companyDomain } = useParams();
  const [profileSelection, setProfileSelection] = useState(null);
  const [selectedMappingContact, setSelectedMappingContact] = useState(null);
  const [drawerType, setDrawerType] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [followUpToEdit, setFollowUpToEdit] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: relationshipMapping,
    isLoading: isRelationshipMappingLoading,
    isFetching: isRelationshipMappingFetching,
  } = useFetchAccountRelationship({
    companyName: companyDomain,
    searchQuery,
  });
  const {
    mutate: refreshRelationshipMapping,
    isPending: isRefreshingRelationshipMapping,
  } = useRefreshAccountRelationship();

  const isInitialRelationshipLoad =
    isRelationshipMappingLoading && !relationshipMapping;

  const closeProfile = useCallback(() => {
    setProfileSelection(null);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerType(null);
    setNoteToEdit(null);
    setFollowUpToEdit(null);
  }, []);

  const handleRefreshRelationshipMapping = useCallback(() => {
    if (!companyDomain || isRefreshingRelationshipMapping) return;

    refreshRelationshipMapping({
      companyName: companyDomain,
      searchQuery,
    });
  }, [
    companyDomain,
    isRefreshingRelationshipMapping,
    refreshRelationshipMapping,
    searchQuery,
  ]);

  useEffect(() => {
    const contacts = relationshipMapping?.contacts;
    if (!contacts?.length) {
      setSelectedMappingContact(null);
      return;
    }
    // Take the fresh list item so title corrections from the detail call show up.
    setSelectedMappingContact(
      (current) =>
        contacts.find(
          (c) => c.relationship_mapping_id === current?.relationship_mapping_id,
        ) ?? contacts[0],
    );
  }, [relationshipMapping?.contacts]);

  const actionsType = [
    {
      id: 1,
      name: "Take Notes",
      icon: <File size={18} className="text-secondary" />,
      description: "Record any recent conversation",
      onClick: () => {
        setNoteToEdit(null);
        setDrawerType("notes");
        setIsDrawerOpen(true);
      },
    },
    {
      id: 2,
      name: "Campaigns",
      icon: <Clipboard size={18} className="text-secondary" />,
      description: "Add contacts to outreach flow",
      onClick: () => {
        setDrawerType("campaigns");
        setIsDrawerOpen(true);
      },
    },
    {
      id: 3,
      name: "Follow-up Reminder",
      icon: <Bell size={18} className="text-secondary" />,
      description: "Schedule your next interaction",
      onClick: () => {
        setFollowUpToEdit(null);
        setDrawerType("followUpReminder");
        setIsDrawerOpen(true);
      },
    },
  ];

  return (
    <>
      {isInitialRelationshipLoad ? (
        <div className="flex items-center justify-center h-[calc(100vh-315px)] w-full">
          <Loader text="Loading relationships..." />
        </div>
      ) : (
        <>
          <div className="flex w-full">
            {/* sideArea with searchbar */}
            <MappingListSidebar
              relationshipMapping={relationshipMapping}
              selectedMappingContact={selectedMappingContact}
              setSelectedMappingContact={setSelectedMappingContact}
              height={height}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              isSearching={isRelationshipMappingFetching && !!searchQuery}
              isRefreshing={isRefreshingRelationshipMapping}
              onRefresh={handleRefreshRelationshipMapping}
            />
            {/* main */}
            <div
              className="w-[78%] px-5 overflow-y-auto"
              style={{
                height: `calc(${height} - 16px)`,
              }}
            >
              {!selectedMappingContact &&
              !relationshipMapping?.contacts?.length ? (
                <div className="flex items-center justify-center h-full min-h-[300px] w-full">
                  <p className="text-xs text-gray-500 font-[Inter]">
                    No contacts found for this account.
                  </p>
                </div>
              ) : (
                <MappingLeftSideArea
                  selectedMappingContact={selectedMappingContact}
                  setShowDraftModal={setShowDraftModal}
                  actionsType={actionsType}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setDrawerType={setDrawerType}
                  setNoteToEdit={setNoteToEdit}
                  setFollowUpToEdit={setFollowUpToEdit}
                />
              )}
            </div>
          </div>
        </>
      )}

      <ProspectProfileModal
        isOpen={!!profileSelection}
        onClose={closeProfile}
        userId={user?.id}
        relationshipMappingId={profileSelection?.relationship_mapping_id}
      />

      <ShowDraftModal
        isOpen={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        contact={selectedMappingContact}
      />

      {drawerType === "notes" && (
        <TakeNoteDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          drawerType={drawerType}
          relationshipMappingId={
            selectedMappingContact?.relationship_mapping_id
          }
          noteToEdit={noteToEdit}
        />
      )}
      {drawerType === "campaigns" && (
        <CampaignDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          drawerType={drawerType}
          relationshipMappingId={
            selectedMappingContact?.relationship_mapping_id
          }
          contactName={selectedMappingContact?.person_name}
        />
      )}

      {drawerType === "followUpReminder" && (
        <FollowUpReminderDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          drawerType={drawerType}
          relationshipMappingId={
            selectedMappingContact?.relationship_mapping_id
          }
          followUpToEdit={followUpToEdit}
        />
      )}
    </>
  );
};

export default RelationshipMapping;
